import { MultiFactorAuthenticationError } from '@pba/client-auth';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Col, Row, Spin, Text, Title } from 'ui';
import { handleAxiosError } from 'common/error-handlers';
import { getRailsAccessToken } from 'common/services';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: fix according to result of #67489
import { useLoginToNodeMutation, useLoginWithTokenMutation } from 'features/users/queries';
import { useHandleMfaError } from '../hooks';
import { useAuthenticateMutation } from '../queries';

type P = {
  onNavigateToCheckCode: ({ tokenToGetCode, tokenToLogin }: { tokenToGetCode?: string; tokenToLogin?: string }) => void;
  onNavigateToPrimaryLogin: () => void;
  onSuccessLogin: () => Promise<void>;
  token?: string;
};

const MagicLinkCheck = ({ onNavigateToCheckCode, onNavigateToPrimaryLogin, onSuccessLogin, token }: P) => {
  const { t } = useTranslation();

  const { error: loginError, mutateAsync: checkTokenValidation } = useLoginWithTokenMutation();
  const { error: nodeLoginError, mutateAsync: nodeLoginAsync } = useLoginToNodeMutation();
  const { mutate: authenticate } = useAuthenticateMutation();
  const { contextHolder, handleError: handleMfaError } = useHandleMfaError({ onNavigateToCheckCode, onSuccessLogin });

  useEffect(() => {
    if (!token) {
      return;
    }

    authenticate(
      {
        async nodeLogin() {
          const railsToken = await getRailsAccessToken();
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- Token will be always defined
          return nodeLoginAsync({ accessToken: railsToken!, strategy: 'pba' });
        },
        async railsLogin() {
          return checkTokenValidation({ token, type: 'magic_token' });
        },
      },
      {
        onError(error) {
          if (error instanceof MultiFactorAuthenticationError) {
            handleMfaError({ error });
          } else {
            handleAxiosError(error);
          }
        },
        async onSuccess() {
          await onSuccessLogin();
        },
      },
    );
  }, [authenticate, checkTokenValidation, handleMfaError, nodeLoginAsync, onSuccessLogin, token]);

  const isMfa = () => nodeLoginError instanceof MultiFactorAuthenticationError;

  return (
    <>
      {/* after cancel mfa2 modal this should be set to false */}
      {(token && !loginError && !nodeLoginError) || isMfa() ? (
        <Spin tip={t('Logging you in')} />
      ) : (
        <Row>
          <Col span={24}>
            <Title level={2} marginBottom type="danger">
              Log in link is invalid
            </Title>
          </Col>

          <Col className="onboarding-magic-link-check__already-sent-text" span={24}>
            <Text>Log in link expired or was already used</Text>
          </Col>

          <Col className="onboarding-magic-link-check__buttons-section" span={24}>
            <Button
              block
              onClick={() => {
                onNavigateToPrimaryLogin();
              }}
            >
              Back to Log in
            </Button>
          </Col>
        </Row>
      )}
      {contextHolder}
    </>
  );
};

export default MagicLinkCheck;
